import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, Download, History, ShieldCheck, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { api, ago, errMsg } from "@/lib/api";
import { Empty, Loading, PageHeader } from "@/components/common";
import { PreferencesForm } from "@/pages/Onboarding";

export function Notifications() {
  const [d, setD] = useState(null);
  const load = () => api.get("/notifications").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };
  return (
    <div>
      <PageHeader eyebrow="In-app notifications" title="Notifications" testId="notifications-heading">
        <Button variant="outline" onClick={readAll} data-testid="mark-all-read-button">Mark all read</Button>
      </PageHeader>
      {!d ? <Loading /> : d.items.length === 0 ? <Empty icon={Bell} title="All caught up" testId="notifications-empty">Reminders, budget alerts and document checks appear here.</Empty> : (
        <ul className="panel divide-y" data-testid="notifications-list">
          {d.items.map((n) => (
            <li key={n.id} className={`flex items-start gap-3 p-4 ${n.read ? "opacity-70" : ""}`} data-testid={`notification-${n.id}`}>
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.read ? "bg-muted" : n.kind === "warning" ? "bg-amber-500" : "bg-brand"}`} />
              <div className="flex-1"><p className="text-sm font-medium">{n.title}</p><p className="text-sm text-muted-foreground">{n.body}</p>
                <p className="mt-1 text-[11px] text-muted-foreground">{ago(n.created_at)}{n.link && <> · <Link to={n.link} className="underline" onClick={() => api.post(`/notifications/${n.id}/read`)}>Open</Link></>}</p></div>
            </li>))}
        </ul>
      )}
    </div>
  );
}

export default function Settings() {
  const { me, reload, signOut } = useAuth();
  const nav = useNavigate();
  const [activity, setActivity] = useState(null);
  const [activityError, setActivityError] = useState("");
  useEffect(() => { api.get("/me/activity").then((r) => setActivity(r.data)).catch((e) => setActivityError(errMsg(e))); }, []);
  const exportData = async () => {
    try {
      const { data } = await api.get("/me/export");
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url; a.download = "ajobf-export.json"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    try { await api.delete("/me"); toast.success("Your profile data was deleted"); await signOut(); nav("/login"); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!me) return <Loading />;
  return (
    <div className="space-y-10">
      <PageHeader eyebrow="Settings" title="Search rules & privacy" testId="settings-heading" />
      <div className="flex items-start gap-3 rounded-xl border bg-card p-5"><ShieldCheck className="shrink-0 text-primary" size={22} /><div><h2 className="text-sm font-semibold">You are in control of every outward action.</h2><p className="mt-1 text-xs leading-relaxed text-muted-foreground">Applications are assisted on employer sites. Recruiter emails stay drafts. There are no connected mailboxes or automatic submission permissions.</p></div></div>
      <PreferencesForm initial={me.preferences} onSaved={reload} />
      <section className="panel space-y-4 p-6" data-testid="privacy-panel">
        <p className="eyebrow">Your data</p>
        <p className="text-sm text-muted-foreground">Export everything AJOBF stores about you, or delete your profile, facts, resumes, documents and tracker. Signed in as {me.email}.</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={exportData} data-testid="export-data-button"><Download className="mr-2 h-4 w-4" />Export my data</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild><Button variant="destructive" data-testid="delete-data-button"><Trash2 className="mr-2 h-4 w-4" />Delete my data</Button></AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>Delete all your AJOBF data?</AlertDialogTitle>
                <AlertDialogDescription>This permanently removes your profile, facts, uploaded resumes, documents, manual jobs, tracker, interviews, saved rules and notifications. It cannot be undone.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={del} data-testid="confirm-delete-data">Delete permanently</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </section>
      <section className="panel overflow-hidden"><div className="flex items-center gap-2 border-b p-5"><History size={18} className="text-primary" /><h2 className="section-title">Your account activity</h2></div>{activityError ? <p role="alert" className="p-5 text-sm text-destructive">{activityError}</p> : !activity ? <Loading /> : activity.length === 0 ? <p className="p-5 text-sm text-muted-foreground">Changes to preferences, search rules and interviews will appear here.</p> : <ul className="max-h-80 divide-y overflow-y-auto">{activity.map((event) => <li key={event.id} className="flex items-center justify-between gap-3 px-5 py-3 text-xs"><span className="capitalize">{event.action.replaceAll(".", " · ").replaceAll("_", " ")}</span><time className="shrink-0 text-[11px] text-muted-foreground" dateTime={event.at}>{new Date(event.at).toLocaleString()}</time></li>)}</ul>}</section>
    </div>
  );
}
